import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import {
	Breadcrumb,
	Input,
	InputGroup,
	PageTitle,
	Pagination,
	SelectInput,
	Template,
} from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { SignupPeriod, SignupRange } from '@models/signups.model'
import {
	useExportSignupsMutation,
	useListSignupsQuery,
} from '@services/LeadsApi'
import { openErrorModal } from '@store/ducks/modal'
import Formatters from '@utils/helpers/Formatters'
import { useQuery } from '@utils/hooks'

const CUSTOM = 'custom'

type PeriodChoice = SignupPeriod | typeof CUSTOM

const periodOptions = [
	{ option: 'Últimas 24 horas', value: '24h' },
	{ option: 'Últimos 7 dias', value: '7d' },
	{ option: 'Últimos 14 dias', value: '14d' },
	{ option: 'Últimos 30 dias', value: '30d' },
	{ option: 'Últimos 3 meses', value: '3m' },
	{ option: 'Período personalizado', value: CUSTOM },
]

const isPeriodChoice = (value: string | null): value is PeriodChoice =>
	periodOptions.some((option) => option.value === value)

// Nome do arquivo baixado: o atalho vira "24h", o intervalo manual vira "inicio_a_fim".
function describe(range: SignupRange) {
	return range.period ? range.period : `${range.startDate}_a_${range.endDate}`
}

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] mt-[32px] items-end flex-wrap',
	divider: 'mt-[32px] mb-[12px]',
	hint: 'text-sm text-gray-500 mt-[8px]',
	select: 'min-w-[200px]',
}

const formatDate = (date: Date) => {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')

	return `${year}-${month}-${day}`
}

function ListSignups() {
	const pages = [{ name: 'Cadastros', href: '#', current: true }]

	const query = useQuery()
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const today = new Date()
	const yesterday = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 1
	)

	const pageQuery = Number(query.get('page')) || 1
	const periodQuery = query.get('period')

	const [period, setPeriod] = useState<PeriodChoice>(
		isPeriodChoice(periodQuery) ? periodQuery : '24h'
	)

	const startDateRef = useRef(query.get('start_date') || formatDate(yesterday))
	const endDateRef = useRef(query.get('end_date') || formatDate(today))
	const [startDate, setStartDate] = useState(startDateRef.current)
	const [endDate, setEndDate] = useState(endDateRef.current)
	const [page, setPage] = useState(pageQuery)

	// A API ignora as datas quando recebe um atalho, então a tela manda um ou o outro.
	const range: SignupRange =
		period === CUSTOM ? { startDate, endDate } : { period }

	const [exportSignups, { isLoading: isExporting }] = useExportSignupsMutation()
	const { data, isFetching } = useListSignupsQuery({ page, ...range })

	const isDateValid = () => {
		if (startDateRef.current > endDateRef.current) {
			dispatch(
				openErrorModal({
					title: 'Erro',
					message: 'A data inicial não pode ser maior que a data final.',
				})
			)

			return false
		}

		return true
	}

	// O filtro vai para a URL: sem isso um F5 devolveria a tela para "últimas 24 horas"
	// e o link que o time manda um para o outro não abriria o mesmo recorte.
	const searchCallback = useCallback(() => {
		const params = new URLSearchParams({ page: '1', period })

		if (period === CUSTOM) {
			params.set('start_date', startDate)
			params.set('end_date', endDate)
		}

		navigate(`/cadastros?${params}`)
	}, [navigate, period, startDate, endDate])

	useEffect(() => {
		searchCallback()
	}, [searchCallback])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	const commitCustomRange = () => {
		if (!isDateValid()) return false

		setStartDate(startDateRef.current)
		setEndDate(endDateRef.current)

		return true
	}

	// Exporta o período que está nos campos, não o da última busca: quem mexe nas
	// datas e clica direto em "Exportar" espera a planilha da janela que está vendo.
	const handleExport = async () => {
		if (period === CUSTOM && !commitCustomRange()) return

		const exported: SignupRange =
			period === CUSTOM
				? { startDate: startDateRef.current, endDate: endDateRef.current }
				: { period }

		try {
			const blob = await exportSignups(exported).unwrap()
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')

			link.href = url
			link.setAttribute('download', `cadastros_${describe(exported)}.xlsx`)
			document.body.appendChild(link)
			link.click()
			link.remove()
			window.URL.revokeObjectURL(url)
		} catch (error) {
			dispatch(
				openErrorModal({
					title: 'Ocorreu um erro',
					message: 'Ocorreu um erro ao exportar os cadastros',
				})
			)
		}
	}

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title='Cadastros' />
			<p className={styles.hint}>
				Usuários que se cadastraram no período e ainda não compraram nenhuma
				turma paga.
			</p>
			<Flex className={styles.searchContainer}>
				<InputGroup label='Período' id='period'>
					<div className={styles.select}>
						<SelectInput
							options={periodOptions}
							selectedValue={period}
							onChange={(option) => setPeriod(option.value as PeriodChoice)}
						/>
					</div>
				</InputGroup>

				{period === CUSTOM && (
					<>
						<InputGroup label='Data inicial' id='startDate'>
							<Input
								type='date'
								defaultValue={startDateRef.current}
								onChange={(e) => {
									startDateRef.current = e.currentTarget.value
								}}
							/>
						</InputGroup>
						<InputGroup label='Data final' id='endDate'>
							<Input
								type='date'
								defaultValue={endDateRef.current}
								onChange={(e) => {
									endDateRef.current = e.currentTarget.value
								}}
							/>
						</InputGroup>
						<Button onClick={commitCustomRange}>Filtrar</Button>
					</>
				)}

				<Button onClick={handleExport} disabled={isExporting}>
					{isExporting ? 'Exportando...' : 'Exportar cadastros'}
				</Button>
			</Flex>

			{isFetching ? (
				<div className='flex justify-center'>
					<Spinner size='md' />
				</div>
			) : (
				<Table
					th={[
						{ label: 'nome' },
						{ label: 'e-mail' },
						{ label: 'telefone' },
						{ label: 'whatsapp' },
						{ label: 'interesse' },
						{ label: 'cadastro' },
					]}
				>
					{data?.data && data.data.length > 0 ? (
						data.data.map(
							({
								id: userId,
								name,
								email,
								phone,
								whatsapp,
								interest,
								createdAt,
							}) => (
								<Tr key={userId}>
									<Td className='w-[30%] px-6'>
										<Link to={`/usuarios/${userId}`}>
											<Flex className='items-center gap-4'>{name}</Flex>
										</Link>
									</Td>
									<Td>
										<Link to={`/usuarios/${userId}`}>
											<Flex className='items-center gap-4'>{email}</Flex>
										</Link>
									</Td>
									<Td>{phone || '-'}</Td>
									<Td>{whatsapp || '-'}</Td>
									<Td>{interest || '-'}</Td>
									<Td>{Formatters.createdAt(createdAt, 3)}</Td>
								</Tr>
							)
						)
					) : (
						<EmptyRow tdAmount={6} message='Nenhum cadastro encontrado' />
					)}
				</Table>
			)}

			<Divider className={styles.divider} />
			{data?.data && data.data.length > 0 && (
				<Pagination
					pageCount={data.meta.lastPage || 0}
					maxAmount={data.meta.total || 0}
					perPage={data.meta.perPage || 0}
				/>
			)}
		</Template>
	)
}

export default ListSignups
